/**
 * Prefilling a Create-Invoice form from a quote — the pure half.
 *
 * WHY THIS EXISTS. Converting a quote used to be one click:
 * `POST /quotes/{id}/convert` created a real financial record and the app
 * navigated to it. On the owner's instruction that must stop — the invoice is
 * now only created when somebody presses Save on a prefilled form, so a person
 * sees and can correct the document before it exists. This module is the
 * mapping that prefill needs, and nothing else: no hooks, no fetches, no React,
 * so both the form and a test can call it.
 *
 * ⚠️ **IT REPRODUCES THE SERVER'S OWN COPY, FIELD FOR FIELD — that is the whole
 * specification.** Every value below was read out of the backend's convert
 * transaction (`insertInvoiceFromQuote` and `copyLinesToInvoice` in
 * `emerge-backend-app/src/modules/quotes/quotes.repository.ts`), not invented
 * here. A prefill that quietly differs from convert produces a document a
 * customer is billed against that disagrees with the one the same button
 * produced last week, and nothing anywhere would report it. When the server's
 * copy changes, this changes with it; do not "improve" a field independently.
 *
 * ⚠️ **`POST /quotes/{id}/convert` ACCEPTS NO OVERRIDES.** Its body schema is
 * `z.object({}).strict()` — `{}` is the only legal body and any key at all,
 * `subject` and `statusId` included, is a `422 Unrecognized key`. So a Save that
 * routes through convert cannot carry the user's edits and must follow up with
 * `PATCH /invoices/{id}`; `CONVERT_FOLLOW_UP_PATCH_KEYS` below is that key list,
 * with the three that are a 422 on an invoice named beside it. A Save that
 * instead calls `createInvoice()` + `createInvoiceProduct()` needs no follow-up,
 * and `quoteId` on the create is what keeps the provenance link (and what makes
 * a later stray convert answer its 409).
 *
 * ### What deliberately does NOT carry across, and why
 *
 *  - **`quoteNumber`** — the CRM's own reference for the QUOTE. The two
 *    documents keep their own references; there is no invoice field it belongs
 *    in.
 *  - **`xeroQuoteNumber` and `xeroQuoteId`** — Xero's references, and they name
 *    a different document. Copying either onto `xeroInvoiceNumber` /
 *    `xeroInvoiceId` would make the invoice claim a Xero identity that belongs
 *    to the quote, which is the same failure as reading a stale
 *    `xeroQuoteNumber` as proof of a link. The server copies neither, and
 *    `InvoiceFormModal` has no key for either.
 *  - **`issueDate` and `validTill`** — NOT reused as `billDate` / `dueDate`.
 *    "When the quote was raised" and "when it lapses" are different facts from
 *    "when this was billed" and "when payment is due"; quietly reusing them puts
 *    a due date in the past. See `billDate` / `dueDate` below for what is used.
 *  - **`stageId`, `billingMode`, `xero`, `cities`, the audit fields** — quote
 *    state with no invoice counterpart. `cities` is derived through the header
 *    project on both records and is never written.
 *
 * ⚠️ **`reference` IS carried, and it is the one that looks like it should not
 * be.** It is the CUSTOMER's purchase-order number — `PO MBAMK10654`, what their
 * finance team matches the payment against — not ours and not Xero's, so it is
 * as true of the invoice as it was of the quote. The backend's INSERT copies it
 * (`reference = :reference`). It is only the other three references that must
 * not travel, which is exactly why they are named one by one above rather than
 * summarised as "the reference fields".
 *
 * ### Three things the server copies that this app cannot express
 *
 * Named because each is a silent loss, not a visible one:
 *
 *  - **the header `projectId`** — `InvoiceFormModal` has no such key, on
 *    purpose (a reachable `projectId` with no control behind it emits `null` on
 *    every edit and an explicit `null` CLEARS). A converted invoice therefore
 *    kept the quote's header project; one created through the form will not, so
 *    it also reports `cities: []`. Lines keep their own projects either way,
 *    which is where the cities and the Xero tracking that matter actually live.
 *  - **`ownerId`** — removed from the invoice form structurally when account
 *    lead replaced owner.
 *  - **`accountLeadId`** — copied server-side as an override; `accountLeadId` in
 *    a write body is a 422, so no client can send it.
 */
import { todayIsoDate } from '@/lib/invoice-utils';
import { addDaysIso } from '@/lib/quote-utils';
import type {
  InvoiceProductCreateInput,
  InvoiceUpdateInput,
  LineInclusionPayload,
  Quote,
  QuoteProduct,
} from '@/lib/types';

/**
 * How long after the bill date an invoice raised from a quote falls due.
 *
 * **A measurement, not a house preference.** The backend chose 30 because 428 of
 * the 487 invoices in the live Xero tenant carry exactly `billDate + 30`, and
 * `INVOICE_PAYMENT_TERM_DAYS` in `quote-convert.ts` is the same constant on the
 * other side of the wire. It is named rather than written inline for the reason
 * the backend gives: a bare `+ 30` in a date expression reads as arithmetic, so
 * nobody asks where it came from.
 */
export const INVOICE_PAYMENT_TERM_DAYS = 30;

/**
 * The header values a Create-Invoice form starts from.
 *
 * **Every field is a STRING, `''` for absent**, because that is what a
 * controlled form holds — not because the API wants strings. The form's own
 * payload builder is what turns `''` back into `null` (or omits the key), and it
 * must keep doing so: `''` and `null` are different instructions on
 * `currencyRate`, `xeroLineAmountType` and both tracking overrides, and this
 * type cannot tell them apart. It is a prefill, not a payload.
 *
 * The key set mirrors `InvoiceFormModal`'s `FormValues` exactly, and the two
 * absences are load-bearing rather than oversights: **there is no `projectId`
 * and no `ownerId`**, both removed structurally so no payload can express a
 * write that would clear a live column. Do not add either here to "complete" the
 * mapping — a key here is a key the form would then have to have.
 */
export interface InvoiceHeaderPrefill {
  subject: string;
  statusId: string;
  contactId: string;
  companyId: string;
  dealId: string;
  billDate: string;
  dueDate: string;
  currency: string;
  currencyRate: string;
  subTotal: string;
  discountTotal: string;
  taxTotal: string;
  grandTotal: string;
  terms: string;
  reference: string;
  xeroLineAmountType: string;
  xeroBrandingThemeId: string;
  xeroTrackingLocation: string;
  xeroTrackingTourType: string;
}

/**
 * The prefill, plus the one thing that is NOT a form value and must not become
 * one.
 *
 * `quoteId` is the provenance link (`d_invoices.quote_id`) the double-convert
 * guard reads, and it appears nowhere in `InvoiceFormModal` — deliberately, per
 * CLAUDE.md: clearing it silently unblocks a duplicate conversion. So it is
 * handed back beside the values rather than inside them, for the create payload
 * to carry once. **Drop it and the invoice is an orphan**: nothing records which
 * quote it was raised from, `GET /invoices?quoteId=N` cannot find it, the quote
 * still looks unbilled, and a later convert would happily raise a second
 * document for the same work.
 */
export interface QuoteInvoicePrefill {
  values: InvoiceHeaderPrefill;
  quoteId: number;
}

export interface QuoteInvoicePrefillOptions {
  /**
   * The id of the `Draft` row of `/invoice-statuses`.
   *
   * The server's convert resolves this row **by name, case-insensitively**, and
   * a missing one rolls the whole conversion back with a 409 naming the row to
   * restore rather than creating a status-less invoice. A pure
   * module cannot do that lookup, so the caller passes the id it resolved from
   * the statuses it already fetched.
   *
   * Omitted or `null` leaves `statusId` empty and the form's own picker decides.
   * That is an honest blank, not a default: guessing an id here would put a
   * status on a financial document that nobody chose.
   */
  draftStatusId?: number | null;
  /**
   * Today as `YYYY-MM-DD`. Defaults to `todayIsoDate()`, which assembles it from
   * LOCAL calendar parts — `toISOString().slice(0,10)` hands back tomorrow for
   * anyone east of Greenwich in their evening. Injectable so a test can pin it.
   */
  today?: string;
  /** Defaults to `INVOICE_PAYMENT_TERM_DAYS`. */
  paymentTermDays?: number;
}

/** `''` for a null, so a controlled input never renders the word "null". */
function text(value: string | null | undefined): string {
  return value ?? '';
}

/** An id as a select's string value; `''` when there is no record. */
function idText(ref: { id: number } | null | undefined): string {
  return ref === null || ref === undefined ? '' : String(ref.id);
}

/**
 * Map a loaded quote onto the header values a Create-Invoice form opens with.
 *
 * ⚠️ **THE FOUR TOTALS ARE PREFILLED, AND ON A QUOTE WITH LINES THEY WILL BE
 * OVERWRITTEN.** They are carried because the server carries them verbatim, and
 * because a quote with no line items has nothing else to say what it is worth —
 * hand-entered header totals are the whole point of the legacy-import case. But
 * an invoice header is written by two competing writers: an invoice PATCH stores
 * exactly what it is given, while ANY line write rewrites all four from the
 * lines. So in a flow that creates the invoice and then adds the copied lines,
 * the first line write replaces these figures with the sum of the lines.
 *
 * Usually that is invisible, because the generated column expressions are
 * identical on `f_quote_products` and `f_invoice_products` and the two arrive at
 * the same figures to the penny. **Where it is NOT invisible is a quote whose
 * stored header disagrees with its own lines** — a Zoho import, or totals typed
 * by hand. `convert` preserves that disagreement (it copies the header and
 * inserts the lines in one statement, never through the line-write path); a
 * create-then-add-lines flow silently repairs it. Neither is wrong, but they are
 * different documents, so a caller that carries lines across should say which it
 * did rather than let the figures move under the person who read them.
 */
export function invoicePrefillFromQuote(
  quote: Quote,
  options: QuoteInvoicePrefillOptions = {},
): QuoteInvoicePrefill {
  const billDate = options.today ?? todayIsoDate();
  const termDays = options.paymentTermDays ?? INVOICE_PAYMENT_TERM_DAYS;

  return {
    quoteId: quote.id,
    values: {
      // Verbatim. `subject` is required on an invoice and on a quote alike, so
      // there is no fallback to write and nothing to derive.
      subject: quote.subject,
      statusId:
        options.draftStatusId === null || options.draftStatusId === undefined
          ? ''
          : String(options.draftStatusId),
      contactId: idText(quote.contact),
      companyId: idText(quote.company),
      dealId: idText(quote.deal),
      /*
       * TODAY, and today + 30 — not the quote's `issueDate` / `validTill`.
       *
       * The pair is also CO-VALIDATED: the server runs `billDate > dueDate` only
       * when it sees both keys in one body, so whichever of the two a person
       * edits, the form must send both. Seeding both together is what makes that
       * possible at all.
       */
      billDate,
      dueDate: addDaysIso(billDate, termDays),
      currency: text(quote.currency),
      /*
       * ⚠️ Carried as the STRING it is, never through `Number()`. It is
       * `DECIMAL(18,6)` and is pushed to Xero verbatim as `Invoice.CurrencyRate`,
       * so a float round-trip lands on a real accounting document. `''` here is
       * the absence of a pin, which is NOT a rate of 1.
       */
      currencyRate: text(quote.currencyRate),
      // `subTotal` and `grandTotal` are NOT NULL on both records; the other two
      // are genuinely nullable and `''` means "not recorded", never zero.
      subTotal: quote.subTotal,
      discountTotal: text(quote.discountTotal),
      taxTotal: text(quote.taxTotal),
      grandTotal: quote.grandTotal,
      terms: text(quote.terms),
      // The CUSTOMER's PO number — see the module header for why this one, alone
      // of the four references, is the one that travels.
      reference: text(quote.reference),
      /*
       * The document enum (`NOTAX`), which is what a quote already holds — never
       * a company's contact-level `NONE`. `''` on the wire means "fall back to
       * the company's Xero sales default", never "no tax", and the same is true
       * of the two tracking overrides below, whose `null` means DERIVE.
       */
      xeroLineAmountType: text(quote.xeroLineAmountType),
      xeroBrandingThemeId: text(quote.xeroBrandingThemeId),
      xeroTrackingLocation: text(quote.xeroTrackingLocation),
      xeroTrackingTourType: text(quote.xeroTrackingTourType),
    },
  };
}

/**
 * Map one quote line onto the body of `POST /invoices/{id}/products`.
 *
 * The return type is `InvoiceProductCreateInput` and that is doing real work:
 * the write schema is `.strict()`, so an object literal carrying a key it does
 * not declare fails at `tsc` here instead of as a 422 on a financial write.
 *
 * ⚠️ **`lineSubtotal`, `lineTax` and `lineTotal` are NEVER emitted.** All three
 * are GENERATED STORED on `f_invoice_products` and sending any one is a 422.
 * They recompute themselves from the copied `quantity`, `unitPrice`, `discount`
 * and `taxRate`, and because the column expressions are defined identically on
 * the quote and invoice tables the copy comes out to the penny of the original.
 * There is nothing to carry and nothing to check.
 *
 * ⚠️ **The read and write shapes differ under the SAME key, on two of these**,
 * which is what makes handing a `QuoteProduct` straight to the API a 422 rather
 * than a type error:
 *
 *  - `cities` is `LineCity[]` (`{id, name, country, sortOrder}`) on the way out
 *    and `cityIds: number[]` on the way in;
 *  - `inclusions` is `LineInclusion[]`
 *    (`{id, name, unitSingular, unitPlural, quantity, sortOrder}`) on the way
 *    out and `{inclusionId, quantity?, sortOrder?}` on the way in — itself
 *    `.strict()`, so posting the read entry back is a 422 on FOUR keys at once
 *    (`name`, `unitSingular`, `unitPlural`, and `id`, whose write name is
 *    `inclusionId`).
 *
 * Both keys are **REPLACE**: sending one replaces the whole set. On a create
 * that is the same as sending it once, which is what this does.
 */
export function invoiceLineFromQuoteLine(line: QuoteProduct): InvoiceProductCreateInput {
  const body: InvoiceProductCreateInput = {
    productId: line.product.id,
    /*
     * ⚠️ Copied INCLUDING its null, and the null must travel as a null.
     * `description` is an OVERRIDE: `null` means the text is derived from the
     * product and the line's cities and keeps following both, a string means
     * somebody typed it and it prints verbatim on the customer's document. A
     * `?? ''` here would freeze an EMPTY override onto the invoice line, which
     * prints no description at all and never recovers one.
     */
    description: line.description,
    /*
     * The LINE's own project, not the document's. Since migration 067 this is
     * the authority for which cities the line may name and for the Xero tracking
     * bucket it reports into, and it is half the line's identity —
     * `(invoice_id, product_id, project_key)` — so the same product may
     * legitimately arrive twice under two projects. `null` is an ordinary state:
     * 27 of 28 products have no projects at all.
     */
    projectId: line.project === null ? null : line.project.id,
    quantity: line.quantity,
    /*
     * ⚠️ Sent EXPLICITLY, `null` included — never omitted.
     *
     * Omitting `unitPrice` tells the server to snapshot the CATALOGUE price, and
     * over the life of a quote the catalogue is often repriced. The figure on
     * the quote line is the snapshot the customer accepted; re-reading it would
     * bill them something they never agreed to, which is the exact failure the
     * snapshot exists to prevent. An explicit `null` is a different fact again —
     * "no price on this line" — and it too has to cross as itself.
     */
    unitPrice: line.unitPrice,
    /* Percentages, 0..100, as the exact decimal strings they are stored as. */
    discount: line.discount,
    taxRate: line.taxRate,
    /*
     * ⚠️ NOT derivable from `taxRate`, which is why it is copied rather than
     * re-resolved. `ZERORATEDOUTPUT`, `NONE` and `EXEMPTOUTPUT` are all 0% in
     * this tenant, so the percentage cannot tell you the code — a zero-rated
     * quote whose code was dropped would reach the customer under a different
     * VAT treatment than the one they agreed to, with both documents looking
     * correct side by side. `null` means "use the company's Xero sales default",
     * never "no tax".
     */
    xeroTaxType: line.xeroTaxType,
    inclusions: inclusionPayloadsFromLine(line.inclusions),
    sortOrder: line.sortOrder,
  };

  /*
   * ⚠️ ONE of `allCities` and `cityIds`, never both — `allCities: true`
   * alongside a non-empty `cityIds` is a 422, deliberately, because there is no
   * correct silent answer when a flag and a list disagree.
   *
   * And the flag MUST cross. A line marked `allCities` stores no explicit city
   * rows at all: its `cities` array is resolved live against the project on
   * every read, so a copy that expanded it into a list would freeze today's set
   * (and one that carried neither would show NO cities), silently, on a document
   * the customer receives. The server's own copy carries the column
   * (`all_project_cities` in `copyLinesToInvoice`); this mirrors it.
   */
  if (line.allCities) {
    body.allCities = true;
  } else {
    // `[]` is always legal, including on a line with no project, and is the
    // honest value for a line that names no city. `null` would be a 422.
    body.cityIds = line.cities.map((city) => city.id);
  }

  return body;
}

/**
 * `LineInclusion[]` (read) → `LineInclusionPayload[]` (write).
 *
 * Split out because the read-to-write rename is the whole trap: `id` becomes
 * `inclusionId`, and `name` / `unitSingular` / `unitPlural` are dropped — they
 * live on the master, and the entry schema is `.strict()`, so passing them
 * through is a 422 naming each one.
 *
 * `quantity` and `sortOrder` are sent explicitly rather than left to their
 * defaults (1, and the array position): a quantity of 3 that fell back to 1
 * would promise the customer a third of what the quote did, and both documents
 * would look complete. **`quantity: 0` is refused** by zod and by a CHECK
 * constraint, so a stored zero cannot arrive here to be copied.
 *
 * Order is preserved as given. The server already sorted the read array by
 * `sortOrder`; re-sorting it would change what the customer reads.
 */
export function inclusionPayloadsFromLine(
  inclusions: QuoteProduct['inclusions'],
): LineInclusionPayload[] {
  return inclusions.map((inclusion) => ({
    inclusionId: inclusion.id,
    quantity: inclusion.quantity,
    sortOrder: inclusion.sortOrder,
  }));
}

/** Map a whole quote's lines, keeping the order they arrived in. */
export function invoiceLinesFromQuoteLines(
  lines: readonly QuoteProduct[],
): InvoiceProductCreateInput[] {
  return lines.map(invoiceLineFromQuoteLine);
}

/**
 * The header keys a follow-up `PATCH /invoices/{id}` would have to carry, if
 * Save routes through `POST /quotes/{id}/convert` rather than
 * `POST /invoices`.
 *
 * ⚠️ **It exists because convert takes NO overrides.** Its body schema is
 * `z.object({}).strict()`; `{}` is the only legal body, and a client trying to
 * smuggle in its own `subject`, its own totals or its own `statusId` gets a
 * `422 Unrecognized key`, not a silently ignored field. So on that route the
 * invoice is created from the quote first and the user's edits are a SECOND
 * write, against a document that already exists — which is a real behavioural
 * difference, not an implementation detail: the invoice is real, and reaches
 * Xero-adjacent state, before the edits land.
 *
 * ⚠️ **Two writable-looking keys are absent and must stay absent** — each is a
 * 422 on an invoice write, so naming one in a patch fails the whole call:
 *
 *  - `quoteNumber` and `xeroQuoteNumber` — quote fields; an invoice's own
 *    counterpart is `xeroInvoiceNumber`, which this form does not write;
 *  - `cityIds` — an invoice has no cities of its own; they derive through the
 *    header project, and cities are written on a LINE.
 *
 * `projectId`, `ownerId` and `accountLeadId` are legal on the API and are absent
 * for a different reason: this app cannot express any of them (see the module
 * header), so a patch built from the form's own values can never name them.
 *
 * ⚠️ **CORRECTION (2026-08-30): `accountLeadId` was listed above as a THIRD 422
 * and it is not one.** Migration 082 gave `d_invoices` a real `account_lead_id`
 * override and the key is on the write schema as `optionalUserId()`
 * (`invoices.schema.ts:200`, verified from backend source); `null` clears the
 * override rather than blanking the field. It is refused only on a DELIVERABLE.
 * The key stays out of every payload here — the owner retired lead-assignment on
 * quotes and invoices in favour of the company's lead shown read-only — but that
 * is a product decision, not the server refusing us, and the two must not be
 * conflated: reading this as a refusal turns a buildable feature into a reported
 * backend gap.
 *
 * ⚠️ **The two dates travel TOGETHER or not at all.** The server runs the
 * `billDate > dueDate` check only when it sees BOTH keys in one body, so a
 * one-sided patch slides a bill date past a stored due date and answers 200.
 *
 * Typed as a tuple of `InvoiceUpdateInput` keys so a rename on the input type
 * breaks this list rather than leaving it quietly wrong.
 */
export const CONVERT_FOLLOW_UP_PATCH_KEYS = [
  'subject',
  'statusId',
  'contactId',
  'companyId',
  'dealId',
  'billDate',
  'dueDate',
  'currency',
  'currencyRate',
  'subTotal',
  'discountTotal',
  'taxTotal',
  'grandTotal',
  'terms',
  'reference',
  'xeroLineAmountType',
  'xeroBrandingThemeId',
  'xeroTrackingLocation',
  'xeroTrackingTourType',
] as const satisfies readonly (keyof InvoiceUpdateInput)[];
